import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useNavigate, useParams } from 'react-router-dom';

import {
  type AvatarUploadRef,
  InputColor,
  initialQueryState,
  showToastMessage,
  updateQueryStateTable,
  useTableStore,
} from 'enable-ui';
import { useFormik } from 'formik';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { RoleKey } from 'modules/auth/core/enum';
import type { Classes } from 'modules/classes/core/type';
import { isWrongAge, validMeasurementInput } from 'modules/learners/utils';

import ClassDefaultIMG from '../../../../assets/icons/class-default.png';

import type { Learner, LearnerFormRequest, Survey, SurveyResult, UserMeasurement } from '../type';

import {
  checkRole,
  getClassPath,
  getExtFileName,
  getInputStatus,
  hasEmptyValues,
  isValidEmail,
  randomString,
  trimObject,
} from 'helpers';
import { UploadFile } from 'helpers/upload-file';
import { useBoolean, useString } from 'hooks';
import { apiCall, useFetchApiToState } from 'hooks/service';
import { cloneDeep, isEmpty, omit } from 'lodash';

import { ERROR_MESSAGE, SUCCESS_MESSAGE } from 'constants/message';
import { PATH } from 'constants/path';
import { TABLE_KEY } from 'constants/table';

import { defaultLearnerForm } from '../constant';
import { LEARNER_DETAIL, SurveyType } from '../enum';
import { formUpdateLearnerSchema } from '../schema';

export const useLearnerDetailController = () => {
  const navigationTo = useNavigate();

  const params = useParams();
  const { id } = params;

  const [activeKey, setActiveKey] = useState<string>(LEARNER_DETAIL.PROFILE);

  const [surveyResult, setSurveyResult] = useState<SurveyResult | null>(null);

  const [classes, setClasses] = useState<{ items: Classes[]; total: number }>({
    items: [],
    total: 0,
  });

  const userProfile = useAuthStore(userProfileSelector);

  const queryState = useTableStore((s) => s.state.queryState[TABLE_KEY.LEARNER_CLASS_RESULT]);

  const institutionId = userProfile?.institution?.id || '';

  const learnerDetailState = useFetchApiToState<any, any>({
    url: `/users/learner/${id}`,
    method: 'GET',
    isLoading: true,
  });

  const learner: Learner = learnerDetailState.state
    ? {
        ...learnerDetailState.state,
        classes: learnerDetailState.state?.classes?.map((el: { class: Classes }) => ({
          class: { ...el.class, thumbnail: el.class?.thumbnail || ClassDefaultIMG },
        })),
      }
    : {};

  const logoRef = useRef<AvatarUploadRef>(null);

  const logoURL = useString('');

  const [logo, setLogo] = useState<File | null>(null);

  const isErrorAvatar = useBoolean(false);

  const isRemoveLogo = useBoolean(false);
  const isAccount = useBoolean(false);

  const searchSelectMonth = useString('');

  const searchSelectYear = useString('');

  const isChangeAvatar = useBoolean(false);

  const confirmPassword = useString('');

  useEffect(() => {
    updateQueryStateTable(TABLE_KEY.LEARNER_CLASS_RESULT, initialQueryState);

    return () => {
      updateQueryStateTable(TABLE_KEY.LEARNER_CLASS_RESULT, initialQueryState);
    };
  }, []);

  useEffect(() => {
    if (!id) {
      return;
    }

    learnerDetailState.fetchApi();

    getSurveys();
  }, []);

  const handleChangeStatus = async () => {
    await apiCall({
      url: `/users/${id}/status`,
      method: 'PATCH',
      isLoading: true,
      showError: true,
    });

    const newLearner = cloneDeep(learnerDetailState.state);

    newLearner.isActive = !newLearner.isActive;
    learnerDetailState.setState(newLearner);

    showToastMessage('success', "Updated client's status successfully!");
  };

  const getList = async (search: string = '') => {
    const result: { items: any; total: number } = await apiCall({
      url: '/user-classes',
      method: 'GET',
      isLoading: true,
      showError: true,
      data: {
        ...queryState,
        search: search,
        userId: id,
      },
    });

    setClasses({
      total: result.total,
      items: result.items.map((el: { class: Classes; totalScore: number }) => ({
        ...el,
        class: { ...el.class, thumbnail: el.class.thumbnail || ClassDefaultIMG },
      })),
    });
  };

  useEffect(() => {
    if (queryState) {
      getList();
    }
  }, [queryState]);

  const getSurveys = async () => {
    const result: Survey = await apiCall({
      method: 'GET',
      url: '/surveys',
      data: {
        type: SurveyType.FUNCTIONAL_SCALE,
      },
    });

    if (result) {
      const res: SurveyResult = await apiCall({
        url: `/surveys/${result.id}/result/${id}`,
        method: 'GET',
      });

      if (res) {
        setSurveyResult(res);
      }
    }
  };

  const formik = useFormik({
    initialValues: defaultLearnerForm,
    validationSchema: formUpdateLearnerSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (!values.institutionId) {
        return;
      }

      setSubmitting(true);

      let avatar = logoURL.value;

      if (values.firstName.length + values.lastName.length > 80) {
        showToastMessage('error', 'Maximum of 80 characters for the name');

        return;
      }

      if (logo) {
        const ext = getExtFileName(logo.name);

        avatar = `learner-${randomString(4)}-${Date.now()}${ext}`;

        await UploadFile(logo, avatar);
      }

      const request: any = {
        ...trimObject(
          omit(values, [
            'height',
            'weight',
            'bmi',
            'bodyFat',
            'skeletalMuscle',
            'visceralFatLevel',
            'restingMetabolism',
          ]),
        ),
        institutionId: values.institutionId,
        measurement: {
          height: values?.height || null,
          weight: values?.weight || null,
          bmi:
            values?.height && values?.weight
              ? Number(
                  (
                    (Number(values.weight) * 10000) /
                    (Number(values.height) * Number(values.height))
                  ).toFixed(2),
                )
              : null,
          bodyFat: values?.bodyFat || null,
          skeletalMuscle: values?.skeletalMuscle || null,
          visceralFatLevel: values?.visceralFatLevel || null,
          restingMetabolism: values?.restingMetabolism || null,
        },

        avatar,
      };

      if (!values.password) {
        delete request.password;
      }

      if (!isChangeAvatar.value && logoURL.value) {
        delete request.avatar;
      }

      request.name = `${values.firstName || ''} ${values.lastName || ''}`.trim();

      if (!values.monthOfBirth) {
        request.monthOfBirth = -1;
      }

      if (!values.yearOfBirth) {
        request.yearOfBirth = -1;
      }

      const result: Learner = await apiCall({
        url: `/users/${id}`,
        data: request,
        method: 'PATCH',
        isLoading: true,
        showError: true,
      });

      if (result?.id) {
        await learnerDetailState.fetchApi();

        showToastMessage('success', 'Updated Client successfully!');
      }
    },
  });

  const { values, isSubmitting, isValid, handleSubmit, handleChange, setFieldValue, setValues } =
    formik;

  const originMeasurement = {
    height: learner?.measurement?.height ? String(learner.measurement.height) : '',
    weight: learner?.measurement?.weight ? String(learner.measurement.weight) : '',
    bodyFat: learner?.measurement?.bodyFat ? String(learner.measurement.bodyFat) : '',
    restingMetabolism: learner?.measurement?.restingMetabolism
      ? String(learner.measurement.restingMetabolism)
      : '',
    skeletalMuscle: learner?.measurement?.skeletalMuscle
      ? String(learner.measurement.skeletalMuscle)
      : '',
    visceralFatLevel: learner?.measurement?.visceralFatLevel
      ? String(learner.measurement.visceralFatLevel)
      : '',
  };

  const newMeasurement = {
    height: values?.height ? String(values.height) : '',
    weight: values?.weight ? String(values.weight) : '',
    bodyFat: values?.bodyFat ? String(values.bodyFat) : '',
    restingMetabolism: values?.restingMetabolism ? String(values.restingMetabolism) : '',
    skeletalMuscle: values?.skeletalMuscle ? String(values.skeletalMuscle) : '',
    visceralFatLevel: values?.visceralFatLevel ? String(values.visceralFatLevel) : '',
  };

  const isValidateMeasurement = useMemo(() => {
    if (JSON.stringify(originMeasurement) === JSON.stringify(newMeasurement)) {
      return true;
    }

    return (
      validMeasurementInput('height', newMeasurement.height) &&
      validMeasurementInput('weight', newMeasurement.weight) &&
      validMeasurementInput('bodyFat', newMeasurement.bodyFat) &&
      validMeasurementInput('skeletalMuscle', newMeasurement.skeletalMuscle) &&
      validMeasurementInput('visceralFatLevel', newMeasurement.visceralFatLevel) &&
      validMeasurementInput('restingMetabolism', newMeasurement.restingMetabolism)
    );
  }, [JSON.stringify(originMeasurement), JSON.stringify(newMeasurement)]);

  const invalidDateOfBirth = useMemo(() => {
    if (values.monthOfBirth === -1 && values.yearOfBirth === -1) {
      return false;
    }

    return Boolean(values.monthOfBirth && Number(values.yearOfBirth) < 0);
  }, [
    values.monthOfBirth,
    values.yearOfBirth,
    // learner?.physicalProfile?.monthOfBirth,
    // learner?.physicalProfile?.yearOfBirth,
  ]);

  const emailStatus: { helperText: string; color: InputColor } = useMemo(() => {
    if (values.email === learner.email) {
      return {
        helperText: '',
        color: 'default',
      }; // Return empty object if form is not dirty or email hasn't changed
    }

    return getInputStatus(
      values?.email || '',
      SUCCESS_MESSAGE.EMAIL,
      isValidEmail(String(values.email).trim()).isValid
        ? ''
        : isValidEmail(String(values.email).trim()).message,
    );
  }, [values.email, learner.email]);

  useEffect(() => {
    if (isEmpty(learner)) {
      return;
    }

    setValues({
      name: learner.name,
      gender:
        learner?.physicalProfile?.gender === 'Unspecified'
          ? 'Male'
          : learner?.physicalProfile?.gender,
      password: '',
      phone: learner.phone || '',
      nickname: learner.nickname,
      firstName: learner.firstName || learner.name,
      lastName: learner.lastName,
      email: learner.email,
      dayOfBirth: learner.physicalProfile?.dayOfBirth,
      monthOfBirth: learner.physicalProfile?.monthOfBirth ?? -1,
      yearOfBirth: learner.physicalProfile?.yearOfBirth ?? -1,
      bodyFat: learner.measurement?.bodyFat ?? null,
      restingMetabolism: learner.measurement?.restingMetabolism ?? null,
      skeletalMuscle: learner.measurement?.skeletalMuscle ?? null,
      visceralFatLevel: learner.measurement?.visceralFatLevel ?? null,
      height: learner?.measurement?.height ?? null,
      weight: learner.measurement?.weight ?? null,
      bmi: learner.measurement?.bmi ?? null,
      institutionId: learner?.institution?.id,
    } as LearnerFormRequest);
    isAccount.setValue(learner.isAccount || false);

    logoURL.setValue(learner.physicalProfile?.avatar || '');
  }, [JSON.stringify(learner)]);

  const onChangeGender = (newGenderKey: string) => () => {
    setFieldValue('gender', newGenderKey);
  };

  const handleChangePhone = (value: string) => {
    setFieldValue('phone', value || '');
  };

  const handleChangeDate = (type: keyof LearnerFormRequest, value: number) => {
    switch (type) {
      case 'dayOfBirth':
        setFieldValue(type, value);

        break;

      case 'monthOfBirth':
        setFieldValue(type, value);

        if (!value) {
          setFieldValue('dayOfBirth', null);
        }

        break;

      case 'yearOfBirth':
        setFieldValue(type, value);

        break;

      default:
        return;
    }
  };

  const handleChangeMeasurement = (type: keyof UserMeasurement, text: string) => {
    const newText = text.replace(/,/g, '');
    const decimal = type === 'height' ? 3 : 2;

    if (
      type === 'restingMetabolism' ||
      type === 'visceralFatLevel' ||
      type === 'weight' ||
      type === 'height' ||
      type === 'bodyFat' ||
      type === 'skeletalMuscle'
    ) {
      const valueSplit = newText.split('.');

      if (valueSplit.length === 1 || (valueSplit.length > 1 && valueSplit[1].length < decimal)) {
        newMeasurement[type] = newText;
        setFieldValue(type, newText);
      }
    }
  };

  const onCancel = () => {
    setValues({
      name: learner.name,
      firstName: learner.firstName || learner.name,
      lastName: learner.lastName,
      gender:
        learner?.physicalProfile?.gender === 'Unspecified'
          ? 'Male'
          : learner?.physicalProfile?.gender,
      password: '',
      phone: learner.phone || '',
      nickname: learner.nickname,
      email: learner.email,
      height: learner?.measurement?.height ?? null,
      weight: learner.measurement?.weight ?? null,
      bmi: learner.measurement?.bmi ?? null,
      dayOfBirth: learner.physicalProfile?.dayOfBirth ?? -1,
      monthOfBirth: learner.physicalProfile?.monthOfBirth ?? -1,
      yearOfBirth: learner.physicalProfile?.yearOfBirth ?? -1,
      bodyFat: learner.measurement?.bodyFat ?? null,
      restingMetabolism: learner.measurement?.restingMetabolism ?? null,
      skeletalMuscle: learner.measurement?.skeletalMuscle ?? null,
      visceralFatLevel: learner.measurement?.visceralFatLevel ?? null,
      institutionId: learner?.institution?.id,
    } as LearnerFormRequest);
    logoURL.setValue(learner.physicalProfile?.avatar || '');
    confirmPassword.setValue('');
  };

  const disabled =
    isSubmitting ||
    !isValid ||
    !isValidateMeasurement ||
    hasEmptyValues({ name: values.name }) ||
    emailStatus.helperText === ERROR_MESSAGE.EMAIL ||
    invalidDateOfBirth ||
    !values.firstName ||
    Number(values.yearOfBirth) < 1 ||
    ((learner.firstName
      ? values.firstName === learner.firstName
      : values.firstName === learner.name) &&
      values.lastName === learner.lastName &&
      (learner?.physicalProfile?.gender === 'Unspecified'
        ? values.gender === 'Male'
        : values.gender === learner?.physicalProfile?.gender) &&
      values.email === learner.email &&
      Number(values?.monthOfBirth) === Number(learner?.physicalProfile?.monthOfBirth) &&
      Number(values?.yearOfBirth) === Number(learner?.physicalProfile?.yearOfBirth) &&
      Number(values?.weight) === Number(learner?.measurement?.weight) &&
      Number(values?.height) === Number(learner?.measurement?.height) &&
      Number(values?.bodyFat) === Number(learner?.measurement?.bodyFat) &&
      Number(values?.restingMetabolism) === Number(learner?.measurement?.restingMetabolism) &&
      Number(values?.visceralFatLevel) === Number(learner?.measurement?.visceralFatLevel) &&
      Number(values?.skeletalMuscle) === Number(learner?.measurement?.skeletalMuscle) &&
      values.password === '' &&
      (!learner.physicalProfile?.avatar ? '' : learner.physicalProfile?.avatar) === logoURL.value &&
      values.phone === learner.phone) ||
    isWrongAge(values.yearOfBirth!) ||
    !isValidPhoneNumber(values.phone) ||
    (values.email ? (isValidEmail(String(values?.email).trim()).isValid ? false : true) : false) ||
    values.password !== confirmPassword.value;

  const disabledWithTrainerRole =
    Number(values?.monthOfBirth) === Number(learner?.physicalProfile?.monthOfBirth) &&
    Number(values?.yearOfBirth) === Number(learner?.physicalProfile?.yearOfBirth) &&
    Number(values?.weight) === Number(learner?.measurement?.weight) &&
    Number(values?.height) === Number(learner?.measurement?.height) &&
    Number(values?.bodyFat) === Number(learner?.measurement?.bodyFat) &&
    Number(values?.restingMetabolism) === Number(learner?.measurement?.restingMetabolism) &&
    Number(values?.visceralFatLevel) === Number(learner?.measurement?.visceralFatLevel) &&
    Number(values?.skeletalMuscle) === Number(learner?.measurement?.skeletalMuscle) &&
    (learner?.physicalProfile?.gender === 'Unspecified'
      ? values.gender === 'Male'
      : values.gender === learner?.physicalProfile?.gender);

  const goBack = () => {
    navigationTo(-1);
  };

  const onTabClick = (key: string) => {
    setActiveKey(key);
  };

  const goToCalendar = (classId: string) => {
    navigationTo(getClassPath(classId, PATH.CLASSES_CALENDAR));
  };

  const onChangeAvatar = (file: File) => {
    setLogo(file);

    logoURL.setValue(URL.createObjectURL(file));

    isChangeAvatar.setValue(true);
  };

  const onErrorAvatar = (file: File) => {
    isErrorAvatar.setValue(Boolean(file));
  };

  const onDeleteLogo = () => {
    logoRef.current?.clearAvatar();
    setLogo(null);

    logoURL.setValue('');

    if (logoURL.value) {
      isRemoveLogo.setValue(true);
    }
  };

  const disabledField = checkRole([RoleKey.TRAINER_INSTITUTION, RoleKey.TRAINER_BIOLOGIC]);

  const onChangeMonths = (item: any) => {
    setFieldValue('monthOfBirth', Number(item.value));
  };

  const onChangeSearchMonths = (value: string) => {
    searchSelectMonth.setValue(value);
  };

  const onChangeYear = (item: any) => {
    setFieldValue('yearOfBirth', Number(item.value));
  };

  const onChangeSearchYears = (value: string) => {
    searchSelectYear.setValue(value);
  };

  const handleConfirmPassword = (event: ChangeEvent<HTMLInputElement>) => {
    confirmPassword.setValue(event.target.value);
  };

  const totalTrainingTime = learnerDetailState.state?.totalTrainingTime ?? 0;

  return {
    learnerId: id,
    ...values,
    learner,
    handleChange,
    handleChangeMeasurement,
    onChangeGender,
    handleChangePhone,
    handleSubmit,
    onCancel,
    disabledField,
    disabled: disabledField ? disabledWithTrainerRole : disabled,
    emailStatus,
    handleChangeDate,
    goBack,
    activeKey,
    onTabClick,
    surveyResult,
    goToCalendar,
    logoURL,
    onChangeAvatar,
    onErrorAvatar,
    onDeleteLogo,
    isErrorAvatar: isErrorAvatar.value,
    logoRef,
    classes,
    queryState,
    isAccount,
    handleChangeStatus,
    onChangeMonths,
    onChangeSearchMonths,
    searchSelectMonth,
    onChangeYear,
    onChangeSearchYears,
    searchSelectYear,
    totalTrainingTime,
    confirmPassword,
    handleConfirmPassword,
  };
};
